import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from '../api/axios';
import type { AuthUser, UserRole } from '../types';
import { useSessionManager } from '../hooks/useSessionManager';
import { SessionTimeoutWarning } from '../components/SessionTimeoutWarning';

type AuthContextValue = {
  user: AuthUser | null;
  loading: boolean;
  passwordChangeRequired: boolean;
  firstTimeSetupRequired: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  getHomeForRole: (role?: UserRole | null) => string;
  setUser: React.Dispatch<React.SetStateAction<AuthUser | null>>;
  setPasswordChangeRequired: React.Dispatch<React.SetStateAction<boolean>>;
  setFirstTimeSetupRequired: React.Dispatch<React.SetStateAction<boolean>>;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
};

export const AuthProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [passwordChangeRequired, setPasswordChangeRequired] = useState(false);
  const [firstTimeSetupRequired, setFirstTimeSetupRequired] = useState(false);
  const [sessionWarningTime, setSessionWarningTime] = useState<number | null>(null);
  const [showSessionWarning, setShowSessionWarning] = useState(false);

  const handleSessionExpired = useCallback(() => {
    setUser(null);
    setPasswordChangeRequired(false);
    setFirstTimeSetupRequired(false);
    setSessionWarningTime(null);
    setShowSessionWarning(false);
    // Redirect to login if not already there
    if (!window.location.pathname.includes('/login')) {
      window.location.href = '/login';
    }
  }, []);

  const handleSessionExpiring = useCallback((timeRemaining: number) => {
    setSessionWarningTime(timeRemaining);
    setShowSessionWarning(true);
  }, []);

  const { updateSessionExpiry, refreshSession, clearSession } = useSessionManager({
    onSessionExpiring: handleSessionExpiring,
    onSessionExpired: handleSessionExpired,
    warningThreshold: 5, // Warn 5 minutes before expiration
    refreshThreshold: 10, // Auto-refresh 10 minutes before expiration
  });

  const getHomeForRole = useCallback((role?: UserRole | null) => {
    switch (role) {
      case 'admin':
        return '/admin';
      case 'teacher':
        return '/teacher';
      case 'student':
        return '/student';
      case 'dev':
        return user?.demo_mode ? '/admin' : '/dev';
      default:
        return '/login';
    }
  }, [user?.demo_mode]);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get('/api/auth/me');
      setUser(res.data.user as AuthUser);
      setPasswordChangeRequired(res.data.password_change_required || false);
      setFirstTimeSetupRequired(res.data.first_time_setup_required || false);
      
      // Update session expiry if provided
      if (res.data.session_expires_at) {
        updateSessionExpiry(res.data.session_expires_at);
      } else {
        // Fallback: check sessionStorage for persisted expiry
        const storedExpiry = sessionStorage.getItem('session_expires_at');
        if (storedExpiry) {
          updateSessionExpiry(storedExpiry);
        }
      }
    } catch (err: any) {
      // Only clear user on 401, ignore network errors
      if (err?.response?.status === 401) {
        clearSession();
        sessionStorage.removeItem('session_expires_at');
        setUser(null);
        setPasswordChangeRequired(false);
        setFirstTimeSetupRequired(false);
      }
    }
  }, [updateSessionExpiry, clearSession]);

  const login = useCallback(async (username: string, password: string) => {
    const res = await api.post('/api/login', { username, password });
    setUser(res.data.user as AuthUser);
    setPasswordChangeRequired(res.data.password_change_required || false);
    setFirstTimeSetupRequired(res.data.first_time_setup_required || false);
    
    // Initialize session expiry (will be updated on next /api/auth/me call)
    // Session lifetime is typically 120 minutes, calculate approximate expiry
    const sessionLifetime = 120; // minutes
    const estimatedExpiry = new Date(Date.now() + sessionLifetime * 60 * 1000);
    updateSessionExpiry(estimatedExpiry);
  }, [updateSessionExpiry]);

  const logout = useCallback(async () => {
    try {
      await api.post('/api/logout');
    } catch (err) {
      // Ignore errors on logout
    } finally {
      clearSession();
      setUser(null);
      setPasswordChangeRequired(false);
      setFirstTimeSetupRequired(false);
      setSessionWarningTime(null);
      setShowSessionWarning(false);
    }
  }, [clearSession]);

  const handleStayLoggedIn = useCallback(async () => {
    setShowSessionWarning(false);
    setSessionWarningTime(null);
    
    // Refresh the session
    const refreshed = await refreshSession();
    if (!refreshed) {
      // Session refresh failed, log out
      handleSessionExpired();
    }
  }, [refreshSession, handleSessionExpired]);

  const handleLogoutFromWarning = useCallback(() => {
    setShowSessionWarning(false);
    setSessionWarningTime(null);
    logout();
  }, [logout]);

  useEffect(() => {
    (async () => {
      await checkAuth();
      setLoading(false);
    })();
  }, [checkAuth]);

  // Listen for session expiration events
  useEffect(() => {
    const handleSessionExpiredEvent = () => {
      handleSessionExpired();
    };

    window.addEventListener('session-expired', handleSessionExpiredEvent);
    return () => {
      window.removeEventListener('session-expired', handleSessionExpiredEvent);
    };
  }, [handleSessionExpired]);

  // Monitor user activity and refresh session periodically
  useEffect(() => {
    if (!user) {
      clearSession();
      return;
    }

    // Refresh session on user activity (mouse movement, clicks, keyboard input)
    let activityTimeout: NodeJS.Timeout;
    const handleActivity = () => {
      clearTimeout(activityTimeout);
      activityTimeout = setTimeout(() => {
        // Refresh session after 5 minutes of activity
        refreshSession();
      }, 5 * 60 * 1000); // 5 minutes
    };

    // Listen for user activity
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial activity trigger
    handleActivity();

    return () => {
      clearTimeout(activityTimeout);
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [user, refreshSession, clearSession]);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    loading,
    passwordChangeRequired,
    firstTimeSetupRequired,
    login,
    logout,
    getHomeForRole,
    setUser,
    setPasswordChangeRequired,
    setFirstTimeSetupRequired,
  }), [getHomeForRole, loading, login, logout, user, passwordChangeRequired, firstTimeSetupRequired]);

  return (
    <AuthContext.Provider value={value}>
      {children}
      {showSessionWarning && sessionWarningTime !== null && (
        <SessionTimeoutWarning
          timeRemaining={sessionWarningTime}
          onStayLoggedIn={handleStayLoggedIn}
          onLogout={handleLogoutFromWarning}
        />
      )}
    </AuthContext.Provider>
  );
};

